import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { useState, useContext } from 'react';
import { countFilters, isExternalLink, replaceValuesOfRouteQuery } from '@/utils';
import Paginator from '../search/Paginator';
import ReportCard from '../report/ReportCard';
import { StatBankResult, StatBankItemType } from '@/api/statbank-data';
import {
  AppInsightsContext,
  EntityCardSize,
  MetadataContext,
  SystemFeaturesContext,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
  UserContext,
} from '../index';
import FolderSelectionDialog from '../scrapbook/dialogs/FolderSelectionDialog';
import { saveToScrapbook } from '@/api/scrapbook-data';
import { useNotification } from '../general/NotificationProvider';
import StatBankFilterOptions from './StatBankFilterOptions';
import classNames from 'classnames';
import StatBankItem from './StatBankItem';
import { LinkModel } from '@/types/LinkModel';

type StatBankSearchResultProps = {
  data: any;
  result: {
    facets: any[];
    results: StatBankResult[];
    totalResults: number;
  };
  pageSize: number;
  loading: boolean;
};

const StatBankSearchResult = ({ data, result, pageSize, loading }: StatBankSearchResultProps) => {
  const t = useTranslations();
  const router = useRouter();
  const { staffPdfLink } = useContext(SystemFeaturesContext);
  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const { pageId } = useContext(MetadataContext);

  const { showNotification } = useNotification();
  const [mobileFiltersPanel, setMobileFiltersPanel] = useState(false);
  const [selectedItems, setSelectedItems] = useState<StatBankItemType[]>([]);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [saveObject, setSaveObject] = useState<any>(null);
  const numberOfFilters = countFilters(router);

  const clearFilters = () => {
    replaceValuesOfRouteQuery(router, {
      searchTerm: router.query.searchTerm,
    });
  };

  const deselectItems = () => {
    setSelectedItems([]);
  };

  const copySelectedItemsToClipboard = () => {
    if (selectedItems.length === 0) {
      alert('No items selected');
      return;
    }

    let formattedHtmlContent = '<html>';
    let formattedContent = '';
    selectedItems.forEach((statElement) => {
      if (statElement.statLink?.href) {
        formattedHtmlContent += `<p>${statElement.number}</p><p><a href="${statElement.statLink.href}" target="_blank">${statElement.description}</a></p><br/>`;
        formattedContent += `${statElement.number}\t\n${statElement.description}\n(${statElement.statLink.href})\n\n`;
      } else {
        formattedHtmlContent += `<p>${statElement.number}</p><p>${statElement.description}</p><br/>`;
        formattedContent += `${statElement.number}\t\n${statElement.description}\n`;
      }
    });
    formattedHtmlContent += '</html>';

    try {
      if (navigator.clipboard && navigator.clipboard.write) {
        navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([formattedHtmlContent], { type: 'text/html' }),
            'text/plain': new Blob([formattedContent], { type: 'text/plain' }),
          }),
        ]);
      }
    } catch (err) {
      console.error('Failed to copy to clipboard: ', err);
    }
  };

  const toggleItemSelection = (item: StatBankItemType) => {
    setSelectedItems((prev) => {
      const exists = prev.find((i) => i.statId === item.statId);
      if (exists) {
        return prev.filter((i) => i.statId !== item.statId);
      } else {
        return [...prev, item];
      }
    });
  };

  const generateHashCode = (text: string) => {
    return text.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
  };

  const handleSaveToScrapbook = (statElement: StatBankItemType, resultItem: StatBankResult) => {
    const content = {
      index: statElement.statId,
      pageId: resultItem.reportId,
      pageTitle: resultItem.reportTitle,
      pageUrl: resultItem.reportUrl,
      numericValue: statElement.number,
      description: statElement.description,
      link: statElement.statLink,
      statSubjects: statElement.statSubjects,
    };
    const linkObject = {
      path: `stat.${content.pageId}.${generateHashCode(statElement.number + statElement.description)}.json`,
      contentType: 'application/x.stylus.stat+json',
      content: JSON.stringify(content),
    };
    setSaveObject(linkObject);
    setShowFolderDialog(true);
  };

  const onReportCardClick = (data: StatBankResult) => {
    appInsights.trackCustomEvent(TrackEventName.STAT_BANK, TrackEventKey.STAT_REPORT, TrackEventType.SELECTED, user, {
      pageId,
      pagePath: router.asPath,
      reportID: data.reportId,
      statGroup: data.items?.[0]?.group,
    });
  };

  const openLink = (link: LinkModel | undefined, statId: string) => {
    if (link) {
      appInsights.trackCustomEvent(TrackEventName.STAT_BANK, TrackEventKey.EMPTY, TrackEventType.CLICK, user, {
        statId,
        pageId,
        statLink: link.href || '',
      });

      if (isExternalLink(link)) {
        window.open(link.href, '_blank');
      } else {
        router.push(link.href);
      }
    }
  };

  return (
    <div className="container overflow-x-hidden">
      {data.displayFilters && (
        <>
          <div className="flex flex-col flex-nowrap items-start justify-between pt-4 pb-7 md:flex-row md:pt-0 md:pb-3">
            <div className="mb-4 w-full md:hidden">
              <button
                className="body-1 flex items-center gap-2 rounded-3xl border border-black/20 px-4 py-2 font-semibold"
                onClick={() => setMobileFiltersPanel(!mobileFiltersPanel)}
              >
                {t('general.$filters')}
                {numberOfFilters > 0 && ` (${numberOfFilters})`}
                {numberOfFilters > 0 && (
                  <>
                    <span className="mx-2 h-4 w-px bg-black/20"></span>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFilters();
                      }}
                    >
                      {t('search.$clearAll')}
                    </span>
                  </>
                )}
              </button>
              {mobileFiltersPanel && (
                <div className="mt-4">
                  <StatBankFilterOptions availableFilters={data.availableFilters} facets={result.facets} />
                </div>
              )}
            </div>
            <div className="pt-10 pb-10 text-[22px] font-light md:text-[40px]">
              {t('search.$searchtotalresults', { totalResults: result.totalResults })}
            </div>
          </div>
        </>
      )}

      {staffPdfLink && (
        <div className="mb-4 flex flex-wrap gap-4 pt-3 lg:gap-22 print:hidden">
          <button
            onClick={deselectItems}
            className="cursor-pointer rounded-full bg-gray-100 px-7 py-2.5 text-sm tracking-[1.25px] text-black hover:bg-gray-200"
          >
            Deselect All
          </button>

          <button
            onClick={copySelectedItemsToClipboard}
            className="cursor-pointer rounded-full bg-gray-100 px-7 py-2.5 text-sm tracking-[1.25px] text-black hover:bg-gray-200"
          >
            Copy Selected Items
          </button>
        </div>
      )}

      {result.results && (
        <>
          <div className="mb-3 flex items-center justify-between pb-3 font-semibold">
            <div className="w-full text-[18px] sm:w-2/3">{t('general.$stats')}</div>
            <div className="ml-3 hidden w-48 text-[18px] sm:block lg:w-[192px] xl:w-[270px]">{t('$report')}</div>
          </div>

          {result.results.map((resultItem, index) => (
            <div
              key={index}
              className={`flex flex-col flex-nowrap justify-between border-b border-black py-3 sm:flex-row ${index === 0 ? 'border-t border-black' : ''}`}
            >
              <div className="w-full sm:w-2/3">
                {resultItem.items?.map((statElement, index2) => {
                  const isSelected = selectedItems.some((i) => i.statId === statElement.statId);
                  return (
                    <StatBankItem
                      key={index2}
                      isSelected={isSelected}
                      loading={resultItem.loading}
                      data={statElement}
                      toggleItemSelection={toggleItemSelection}
                      handleSaveToScrapbook={() => handleSaveToScrapbook(statElement, resultItem)}
                      onClick={() => openLink(statElement.statLink, statElement.statId)}
                      className={classNames(index2 !== 0 ? 'mt-2 border-t border-[#c4c4c4]' : '')}
                    />
                  );
                })}
                {resultItem.loading && (
                  <div className="flex items-center py-3">
                    <div className="h-10 w-1/4 animate-pulse bg-gray-300"></div>
                    <div className="ml-3 h-16 flex-1 animate-pulse bg-gray-300"></div>
                  </div>
                )}
              </div>

              <div className="mt-0 ml-3 hidden w-48 sm:block lg:w-[192px] xl:w-[270px]">
                <ReportCard
                  data={
                    {
                      reportId:
                        typeof resultItem.reportId === 'string' ? parseInt(resultItem.reportId) : resultItem.reportId,
                      title: resultItem.reportTitle,
                      reportUrl:
                        typeof resultItem.reportUrl === 'string'
                          ? { href: resultItem.reportUrl, text: resultItem.reportTitle }
                          : resultItem.reportUrl,
                      image:
                        typeof resultItem.reportImage === 'string'
                          ? { url: resultItem.reportImage }
                          : resultItem.reportImage,
                      publishedDate:
                        typeof resultItem.publishedDate === 'string'
                          ? new Date(resultItem.publishedDate)
                          : resultItem.publishedDate,
                      directory:
                        typeof resultItem.directory === 'string'
                          ? { name: resultItem.directory, url: '', id: '', key: '', group: '', parentName: '' }
                          : resultItem.directory
                            ? {
                                ...resultItem.directory,
                                url: resultItem.directory.url || '',
                                id: resultItem.directory.id || '',
                                key: resultItem.directory.key || '',
                                group: resultItem.directory.group || '',
                                parentName: resultItem.directory.parentName || '',
                              }
                            : { name: '', url: '', id: '', key: '', group: '', parentName: '' },
                      isLegacy: resultItem.isLegacy,
                      anchorTextColour: resultItem.anchorTextColour || '',
                      isLoading: resultItem.loading,
                      hideTitleOnAnchor: true,
                    } as any
                  }
                  size={EntityCardSize.AUTO}
                  hideTitle={true}
                  hideViewed={true}
                  hideTopContent={true}
                  onClick={() => onReportCardClick(resultItem)}
                />
              </div>

              <div className="mt-0 block w-full sm:hidden">
                <details className="border-t border-black">
                  <summary className="cursor-pointer py-3 text-[18px] font-semibold">{t('$report')}</summary>
                  <div className="py-3">
                    <ReportCard
                      data={
                        {
                          reportId:
                            typeof resultItem.reportId === 'string'
                              ? parseInt(resultItem.reportId)
                              : resultItem.reportId,
                          title: resultItem.reportTitle,
                          reportUrl:
                            typeof resultItem.reportUrl === 'string'
                              ? { href: resultItem.reportUrl, text: resultItem.reportTitle }
                              : resultItem.reportUrl,
                          image:
                            typeof resultItem.reportImage === 'string'
                              ? { url: resultItem.reportImage }
                              : resultItem.reportImage,
                          publishedDate:
                            typeof resultItem.publishedDate === 'string'
                              ? new Date(resultItem.publishedDate)
                              : resultItem.publishedDate,
                          directory:
                            typeof resultItem.directory === 'string'
                              ? { name: resultItem.directory, url: '', id: '', key: '', group: '', parentName: '' }
                              : resultItem.directory
                                ? {
                                    ...resultItem.directory,
                                    url: resultItem.directory.url || '',
                                    id: resultItem.directory.id || '',
                                    key: resultItem.directory.key || '',
                                    group: resultItem.directory.group || '',
                                    parentName: resultItem.directory.parentName || '',
                                  }
                                : { name: '', url: '', id: '', key: '', group: '', parentName: '' },
                          isLegacy: resultItem.isLegacy,
                          anchorTextColour: resultItem.anchorTextColour || '',
                          isLoading: resultItem.loading,
                          hideTitle: true,
                          hideTitleOnAnchor: true,
                        } as any
                      }
                      size={EntityCardSize.SMALL}
                      hideTopContent={true}
                      onClick={() => onReportCardClick(resultItem)}
                    />
                  </div>
                </details>
              </div>
            </div>
          ))}
        </>
      )}

      {result.results && data.displayFilters && (
        <div className="w-full pt-10 pb-10">
          <Paginator
            pageSize={pageSize}
            totalResults={result.totalResults}
            goToElement="#stat-bank-filters"
            queryMode={true}
          />
        </div>
      )}

      {result.totalResults === 0 && !loading && data.displayFilters && (
        <div className="w-full py-4">{t('statBank.$statBankNoResult')}</div>
      )}

      <FolderSelectionDialog
        isOpen={showFolderDialog}
        onClose={() => setShowFolderDialog(false)}
        onSave={async (folderId: number) => {
          try {
            const result = await saveToScrapbook(folderId, saveObject, user?.userId);
            if (result?.succeeded) {
              showNotification(result.message || 'Stat saved to scrapbook successfully', 'savesuccess');
            } else {
              showNotification('Call to scrapbook server failed.', 'error');
            }
          } catch {
            showNotification('Call to scrapbook server failed.', 'error');
          }
        }}
        reportTitle="Save Stat to Scrapbook"
      />
    </div>
  );
};

export default StatBankSearchResult;
